import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Check, X, Mail, Download, Eye, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPE_LABELS = {
  'veterinary_health_certificate': 'Vet Health Certificate',
  'vaccination_records': 'Vaccination Records',
  'microchip_documentation': 'Microchip Documentation',
  'pet_passport': 'Pet Passport',
  'import_permit': 'Import Permit',
  'rabies_titer_test': 'Rabies Titer Test',
  'pet_photos': 'Pet Photos',
  'owner_passport': 'Owner Passport',
  'other': 'Other'
};

export const DocumentsList = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const [documents, setDocuments] = useState([]);
  const [relocations, setRelocations] = useState({});
  const [clients, setClients] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, relocRes, clientsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/documents`, {
          headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
        }),
        fetch(`${API_URL}/api/admin/relocations`, {
          headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
        }),
        fetch(`${API_URL}/api/admin/clients`, {
          headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
        })
      ]);

      const docsData = await docsRes.json();
      const relocData = await relocRes.json();
      const clientsData = await clientsRes.json();

      // Create maps for quick lookup
      const relocMap = {};
      relocData.forEach(r => { relocMap[r.id] = r; });
      
      const clientMap = {};
      clientsData.forEach(c => { clientMap[c.id] = c; });

      setDocuments(docsData);
      setRelocations(relocMap);
      setClients(clientMap);
    } catch (error) {
      logger.error('Error fetching data:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const updateDocumentStatus = async (docId, status, notes = '') => {
    try {
      const response = await fetch(`${API_URL}/api/admin/documents/${docId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`
        },
        body: JSON.stringify({ status, admin_notes: notes })
      });

      if (response.ok) {
        toast.success(`Document ${status}`);
        fetchData();
      }
    } catch (error) {
      logger.error('Error updating document:', error);
      toast.error('Failed to update document');
    }
  };

  const toggleSelectDoc = (docId) => {
    setSelectedDocs(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const filteredDocuments = documents.filter(doc => {
    if (filterStatus === 'all') return true;
    return doc.status === filterStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
              <p className="mt-1 text-sm text-gray-600">
                Review and manage client-submitted documents
              </p>
            </div>
            {selectedDocs.length > 0 && (
              <Button
                onClick={() => setShowEmailModal(true)}
                className="flex items-center gap-2"
                data-testid="email-selected-btn"
              >
                <Mail className="w-4 h-4" />
                Email Selected ({selectedDocs.length})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Filters & Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Total Documents</div>
            <div className="text-2xl font-bold text-gray-900">{documents.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Pending Review</div>
            <div className="text-2xl font-bold text-yellow-600">
              {documents.filter(d => d.status === 'pending').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Approved</div>
            <div className="text-2xl font-bold text-green-600">
              {documents.filter(d => d.status === 'approved').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Rejected</div>
            <div className="text-2xl font-bold text-red-600">
              {documents.filter(d => d.status === 'rejected').length}
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              data-testid="document-filter-select"
            >
              <option value="all">All Documents</option>
              <option value="pending">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Documents Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No documents found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDocs(filteredDocuments.map(d => d.id));
                          } else {
                            setSelectedDocs([]);
                          }
                        }}
                        checked={selectedDocs.length === filteredDocuments.length && filteredDocuments.length > 0}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Document
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Pet & Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDocuments.map((doc) => {
                    const relocation = relocations[doc.relocation_id];
                    const client = clients[doc.client_id];
                    
                    return (
                      <tr key={doc.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedDocs.includes(doc.id)}
                            onChange={() => toggleSelectDoc(doc.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {DOCUMENT_TYPE_LABELS[doc.document_type]}
                              </div>
                              <div className="text-xs text-gray-500">{doc.file_name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {relocation?.pet_name || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {client ? `${client.first_name} ${client.last_name}` : 'Unknown Client'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(doc.uploaded_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(doc.status)}`}>
                            {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <a
                              href={`${API_URL}${doc.file_path.replace('/app/backend', '')}`}
                              download
                              className="text-blue-600 hover:text-blue-700"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                            {doc.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => updateDocumentStatus(doc.id, 'approved')}
                                  className="text-green-600 hover:text-green-700"
                                  title="Approve"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => updateDocumentStatus(doc.id, 'rejected')}
                                  className="text-red-600 hover:text-red-700"
                                  title="Reject"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6">
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
            ← Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <EmailDocumentsModal
          documentIds={selectedDocs}
          documents={documents}
          relocations={relocations}
          clients={clients}
          password={password}
          onClose={() => {
            setShowEmailModal(false);
            setSelectedDocs([]);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// Email Modal Component
const EmailDocumentsModal = ({ documentIds, documents, relocations, clients, password, onClose }) => {
  const [toEmail, setToEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const selectedDocuments = documents.filter(d => documentIds.includes(d.id));

  const handleSend = async () => {
    if (!toEmail) {
      toast.error('Please enter recipient email');
      return;
    }

    setSending(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/documents/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${btoa(':' + password)}`
        },
        body: JSON.stringify({
          document_ids: documentIds,
          to_email: toEmail,
          message: message
        })
      });

      if (response.ok) {
        toast.success('Documents emailed successfully!');
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      logger.error('Email error:', error);
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Email Documents</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Selected Documents */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <p className="text-sm font-medium text-blue-900 mb-2">
              Sending {selectedDocuments.length} document(s):
            </p>
            <ul className="text-xs text-blue-800 space-y-1">
              {selectedDocuments.slice(0, 3).map(doc => (
                <li key={doc.id}>• {DOCUMENT_TYPE_LABELS[doc.document_type]} - {doc.file_name}</li>
              ))}
              {selectedDocuments.length > 3 && (
                <li>... and {selectedDocuments.length - 3} more</li>
              )}
            </ul>
          </div>

          {/* Recipient Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipient Email *
            </label>
            <input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="government@example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Additional Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Message (Optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Add any additional information..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1" disabled={sending}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending} className="flex-1 flex items-center justify-center gap-2">
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Send Email
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
